import {HocuspocusProvider} from '@hocuspocus/provider';
import {handleServerMessage} from 'helpers/serverMessageHandler';
import {useEffect, useRef, useState} from 'react';
import {getCollaborationUrl, isCollabServerEnabledForPage} from 'utilities';
import * as Y from 'yjs';

export interface User {
  id: string;
  ldap: string;
  name: string;
  role?: string;
  level?: string;
  avatar?: string;
  pageKey?: string;
}

const getUsersFromAwareness = (awareness: {getStates: () => Map<number, any>}) => {
  const states = Array.from(awareness.getStates().values());
  return states.map((state: any) => state.user).filter((u): u is User => u && u.id && u.name);
};

export const usePresenceAwareness = ({
  token,
  url = getCollaborationUrl(),
  pageKey,
  user,
  onUsersChange,
}: {
  token?: string;
  url?: string;
  pageKey?: string;
  user?: User;
  onUsersChange?: (users: User[]) => void;
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const providerRef = useRef<any>(null);
  const docRef = useRef<Y.Doc | null>(null);
  const disconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use a ref to store the callback to avoid dependency issues
  const onUsersChangeRef = useRef(onUsersChange);

  // Update the ref when the callback changes
  useEffect(() => {
    onUsersChangeRef.current = onUsersChange;
  }, [onUsersChange]);

  useEffect(() => {
    if (!(pageKey && isCollabServerEnabledForPage(pageKey))) {
      return;
    }
    if (!token || !user) {
      setError(new Error('Authentication token and user info are required'));
      return;
    }

    // Use a single document for site-wide presence
    const doc = new Y.Doc();
    docRef.current = doc;

    const provider = new HocuspocusProvider({
      url,
      name: 'site-presence', // Single document for all site presence
      document: doc,
      token,
      onConnect: () => {
        console.log('Connected to presence server');
        setIsConnected(true);
        setError(null);
        queueMicrotask(() => {
          try {
            if (providerRef.current !== provider) {
              return;
            }
            if (providerRef.current?.awareness) {
              onUsersChangeRef.current?.(getUsersFromAwareness(providerRef.current.awareness));
            }
          } catch (e) {
            console.error('Error handling initial awareness state:', e);
          }
        });
      },
      onDisconnect: () => {
        console.log('Disconnected from presence server');
        setIsConnected(false);
      },
      onAuthenticationFailed: ({reason}: {reason: string}) => {
        console.error('Presence authentication failed:', reason);
        const authError = new Error(`Authentication failed: ${reason}`);
        setError(authError);
      },
      onStateless: (event: {payload: string}) => handleServerMessage(event),
      // Add error handling for connection failures
      onClose: () => {
        console.log('Presence connection closed');
        setIsConnected(false);
      },
    });

    providerRef.current = provider;

    // Handle tab visibility changes with a delay to avoid churn
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Wait 30 seconds before disconnecting
        disconnectTimerRef.current = setTimeout(() => {
          console.log('Tab hidden for 30s - disconnecting presence');
          provider.disconnect();
        }, 30000);
      } else {
        // Cancel disconnect if they come back quickly
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        provider.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check if tab is already hidden on mount
    if (document.hidden) {
      disconnectTimerRef.current = setTimeout(() => {
        provider.disconnect();
      }, 30000);
    }

    // Set up awareness for user presence
    if (provider.awareness) {
      const awareness = provider.awareness;
      let emittedInitialState = false;

      // Listen for awareness changes
      const handleAwarenessChange = () => {
        emittedInitialState = true;
        try {
          // The deduplication should happen at the UI level based on business logic
          onUsersChangeRef.current?.(getUsersFromAwareness(awareness));
        } catch (e) {
          console.error('Error handling awareness change:', e);
          // Don't throw the error to prevent React Router errors
        }
      };

      awareness.on('change', handleAwarenessChange);

      // Set initial user state after registering the listener, then emit a snapshot if the
      // awareness implementation did not emit one synchronously.
      awareness.setLocalStateField('user', {
        ...user,
        pageKey,
      });
      if (!emittedInitialState) {
        handleAwarenessChange();
      }

      return () => {
        try {
          if (disconnectTimerRef.current) {
            clearTimeout(disconnectTimerRef.current);
            disconnectTimerRef.current = null;
          }
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          awareness.off('change', handleAwarenessChange);
          provider.destroy();
          providerRef.current = null;
          docRef.current = null;
        } catch (e) {
          console.error('Error cleaning up presence provider:', e);
        }
      };
    }

    return () => {
      try {
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        provider.destroy();
        providerRef.current = null;
        docRef.current = null;
      } catch (e) {
        console.error('Error destroying presence provider:', e);
      }
    };
  }, [token, url, user, pageKey]);

  return {
    isConnected,
    error,
  };
};
